import {
  ExtractDocumentTypeFromTypedRxJsonSchema,
  RxJsonSchema,
  toTypedRxJsonSchema,
} from 'rxdb'

import {
  AssessmentConsultationReason,
  AssessmentState,
  CareLevel,
  CareServiceType,
  ConsultationParticipant,
  Gender,
  LifeForm,
  MedicalTreatment,
  Orders,
  PatientHasCareLevel,
  RelevantContacts,
  SupportWithCare,
} from '@/gql/graphql.ts'
import { addNullToEnum } from '@/system/rxdb/utils.ts'

export const assessment = {
  version: 0,
  title: 'Assessment',
  primaryKey: 'rowId',
  type: 'object',
  properties: {
    rowId: {
      maxLength: 36,
      type: 'string',
    },
    updatedAt: {
      type: 'string',
    },
    createdAt: {
      type: 'string',
    },
    state: {
      type: 'string',
      enum: Object.values(AssessmentState),
    },
    patientId: {
      type: ['string', 'null'],
    },
    consultationReason: {
      type: ['string', 'null'],
      enum: addNullToEnum(AssessmentConsultationReason),
    },
    consultationReasonOther: {
      type: ['string', 'null'],
    },
    patientInsuranceNumber: {
      type: ['string', 'null'],
    },
    patientInsuranceName: {
      type: ['string', 'null'],
    },
    patientInsuranceType: {
      type: ['string', 'null'],
    },
    patientGender: {
      type: ['string', 'null'],
      enum: addNullToEnum(Gender),
    },
    patientTitle: {
      type: ['string', 'null'],
    },
    patientName: {
      type: ['string', 'null'],
    },
    patientSurname: {
      type: ['string', 'null'],
    },
    patientBirthdayAt: {
      type: ['string', 'null'],
    },
    patientHasCareLevel: {
      type: ['string', 'null'],
      enum: addNullToEnum(PatientHasCareLevel),
    },
    careServiceType: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareServiceType),
    },
    patientCareLevel: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareLevel),
    },
    patientCareLevelNotes: {
      type: ['string', 'null'],
    },
    patientFinancialSituation: {
      type: ['string', 'null'],
    },
    patientAddress: {
      type: ['object', 'null'],
      properties: {
        street: {
          type: ['string', 'null'],
        },
        houseNumber: {
          type: ['string', 'null'],
        },
        postalCode: {
          type: ['string', 'null'],
        },
        city: {
          type: ['string', 'null'],
        },
      },
    },
    patientPhoneNumber: {
      type: ['string', 'null'],
    },
    patientDiseases: {
      type: ['string', 'null'],
    },
    consultationAt: {
      type: ['string', 'null'],
    },
    consultationBeginAt: {
      type: ['string', 'null'],
    },
    consultationEndAt: {
      type: ['string', 'null'],
    },
    consultationParticipants: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(ConsultationParticipant),
      },
    },
    consultationParticipantsOther: {
      type: ['string', 'null'],
    },
    contactPartner: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactDaughterSon: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactDaughterInLawSonInLaw: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactGrandchild: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactParent: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactFriend: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactNeighbor: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactSupervisor: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactGeneralPractitioner: {
      type: ['array', 'null'],
      items: {
        type: ['object', 'null'],
        properties: {
          name: {
            type: ['string', 'null'],
          },
          address: {
            type: ['string', 'null'],
          },
          phoneNumber: {
            type: ['string', 'null'],
          },
          healthInsuranceFund: {
            type: ['string', 'null'],
          },
          healthInsuranceNumber: {
            type: ['string', 'null'],
          },
          healthInsuranceType: {
            type: ['string', 'null'],
          },
        },
      },
    },
    contactSpecialistDoctor: {
      type: ['array', 'null'],
      items: {
        type: ['object', 'null'],
        properties: {
          name: {
            type: ['string', 'null'],
          },
          address: {
            type: ['string', 'null'],
          },
          phoneNumber: {
            type: ['string', 'null'],
          },
          healthInsuranceFund: {
            type: ['string', 'null'],
          },
          healthInsuranceNumber: {
            type: ['string', 'null'],
          },
          healthInsuranceType: {
            type: ['string', 'null'],
          },
        },
      },
    },
    contactNursingService: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    contactOther: {
      type: ['object', 'null'],
      properties: {
        name: {
          type: ['string', 'null'],
        },
        address: {
          type: ['string', 'null'],
        },
        phoneNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceFund: {
          type: ['string', 'null'],
        },
        healthInsuranceNumber: {
          type: ['string', 'null'],
        },
        healthInsuranceType: {
          type: ['string', 'null'],
        },
      },
    },
    supportWithCare: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(SupportWithCare),
      },
    },
    supportWithCareOther: {
      type: ['string', 'null'],
    },
    caregiverRegistered: {
      type: ['string', 'null'],
    },
    medicalTreatment: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(MedicalTreatment),
      },
    },
    lifeForm: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: addNullToEnum(LifeForm),
      },
    },
    lifeFormOther: {
      type: ['string', 'null'],
    },
    orders: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: addNullToEnum(Orders),
      },
    },
    relevantContacts: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: addNullToEnum(RelevantContacts),
      },
    },
    relevantContactsOther: {
      type: ['string', 'null'],
    },
    assignmentId: {
      ref: 'assignment',
      type: ['string', 'null'],
    },
  },
  required: ['rowId', 'updatedAt', 'state'],
} as const

const schemaTyped = toTypedRxJsonSchema(assessment)

export type Assessment = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>

export const assessmentSchema: RxJsonSchema<Assessment> = assessment
